/**
 * LifeAdviceActionConfirm — confirmation dialog for LifeAdvice writes.
 *
 * The design-system `components/help/ActionConfirm` is registry-driven: it
 * looks up copy by `actionId` in src/data/help-content.json and renders
 * nothing when the id is missing. LifeAdvice's confirmations need per-row copy
 * (the pillar's name, how many prompts and member entries hang off it), so this
 * takes its copy via props instead — the same choice, and the same visual
 * language, as quill/common/QuillActionConfirm.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { colors, typography } from '../../../theme/designTokens';

export interface LifeAdviceActionConfirmProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  /** Optional secondary line under the message. */
  detail?: React.ReactNode;
  /** Persistent caveat rendered in a warning-tinted box. */
  caption?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Burgundy treatment for wider-blast-radius actions (deactivate, delete). */
  destructive?: boolean;
  loading?: boolean;
  pendingLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const LifeAdviceActionConfirm: React.FC<LifeAdviceActionConfirmProps> = ({
  open,
  title,
  message,
  detail,
  caption,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  pendingLabel = 'Working…',
  onConfirm,
  onCancel,
}) => (
  <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
    <DialogTitle
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontFamily: typography.fonts.display,
        color: destructive ? colors.error.dark : 'text.primary',
      }}
    >
      <WarningAmberIcon sx={{ color: destructive ? colors.error.main : colors.warning.main }} />
      {title}
    </DialogTitle>

    <DialogContent dividers>
      <Typography variant="body1" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
        {message}
      </Typography>

      {detail && <Box sx={{ mt: 1.5 }}>{detail}</Box>}

      {caption && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: colors.warning[50],
            border: `1px solid ${colors.warning.light}`,
          }}
        >
          <Typography variant="body2" sx={{ color: colors.warning.dark }}>
            {caption}
          </Typography>
        </Box>
      )}
    </DialogContent>

    <DialogActions sx={{ p: 2, gap: 1 }}>
      <Button onClick={onCancel} variant="outlined" disabled={loading}>
        {cancelLabel}
      </Button>
      <Button
        onClick={onConfirm}
        disabled={loading}
        variant="contained"
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{
          backgroundColor: destructive ? colors.error.main : colors.warning.main,
          color: destructive ? '#FFFFFF' : '#1B1F23',
          '&:hover': {
            backgroundColor: destructive ? colors.error.dark : colors.warning.dark,
          },
        }}
      >
        {loading ? pendingLabel : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export default LifeAdviceActionConfirm;
